import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Select,
  Box,
  ChakraProps,
  Center,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { getActiveCountryByCode } from '../calculation/calculations';
import {
  GeneralRegion,
  ACTIVE_COUNTRIES,
  ActiveCountryCode,
} from '../configs/countries';

export type Props = {
  onChange: (countryCode: ActiveCountryCode, regionKey: GeneralRegion) => void;
  defaultCountry: ActiveCountryCode;
  defaultRegion: GeneralRegion;
} & ChakraProps;

export const CountrySelector = ({
  onChange,
  defaultCountry,
  defaultRegion,
  ...rest
}: Props) => {
  const defaultCountryConfig = getActiveCountryByCode(defaultCountry);

  const [
    currentCountryCode,
    setCurrentCountryCode,
  ] = useState<ActiveCountryCode>(defaultCountryConfig.code);

  const [currentRegionKey, setCurrentRegionKey] = useState<GeneralRegion>(
    defaultRegion || 'DEFAULT',
  );

  const _onChange = (
    countryCode: ActiveCountryCode,
    regionKey: GeneralRegion,
  ): void => {
    onChange(countryCode, regionKey);
    setCurrentCountryCode(countryCode);
    setCurrentRegionKey(regionKey);
  };

  return (
    <CountrySelectorStateless
      boxProps={rest}
      onChange={_onChange}
      countryCode={currentCountryCode}
      regionKey={currentRegionKey}
    />
  );
};

type CountrySelectorStatelessProps = {
  onChange: (countryCode: ActiveCountryCode, regionKey: GeneralRegion) => void;
  countryCode: ActiveCountryCode;
  regionKey: GeneralRegion;
  boxProps: ChakraProps;
};

export const CountrySelectorStateless = ({
  onChange,
  countryCode,
  regionKey,
  boxProps,
}: CountrySelectorStatelessProps) => {
  // not quite statelses due to useDisclosure etc
  // but it doesn't store any form data state
  // about country/region

  const { isOpen, onOpen, onClose } = useDisclosure();
  const showRegionLabel = regionKey !== 'DEFAULT';

  const country = getActiveCountryByCode(countryCode);
  const currentCountryRegions = country.regionKeys;

  const onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value as ActiveCountryCode;
    const country = getActiveCountryByCode(countryCode);
    const currentCountryRegions = country.regionKeys;
    const newRegion = Array.from(currentCountryRegions).includes(regionKey)
      ? regionKey
      : currentCountryRegions[0];
    onChange(countryCode, newRegion);
  };

  const onCountryRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(countryCode, e.target.value as GeneralRegion);
  };

  return (
    <Box {...boxProps}>
      <Text as="p">
        Looks like you're in:{' '}
        <Text as="span" mr="5" fontWeight={600}>
          {getActiveCountryByCode(countryCode).name}
          {showRegionLabel ? `, ${regionKey}` : ''}
        </Text>
        <Button onClick={onOpen} variant="link" color="black">
          Change my location
        </Button>
      </Text>
      <Modal
        blockScrollOnMount={false}
        isCentered
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update settings</ModalHeader>
          <ModalCloseButton />
          <Center>
            <ModalBody>
              <Select onChange={onCountryChange} mb="10" value={countryCode}>
                {ACTIVE_COUNTRIES.map(({ name, code }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </Select>
              {currentCountryRegions && currentCountryRegions.length > 1 && (
                <Select
                  onChange={onCountryRegionChange}
                  mb="10"
                  value={regionKey}
                >
                  {currentCountryRegions.map((name: string) => {
                    return (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    );
                  })}
                </Select>
              )}
            </ModalBody>
          </Center>
        </ModalContent>
      </Modal>
    </Box>
  );
};
